const DEFAULT_ALLOWED_URL_SCHEMES: string[] = [
  'http:',
  'https:',
  'mailto:',
  'tel:',
  'sms:'
];

/**
 * Sanitizes URLs for use in href attributes to prevent javascript: protocol attacks.
 *
 * Allowlists safe URL schemes and rejects potentially dangerous ones.
 * Returns '#' for URLs with blocked schemes. Returns undefined for falsey values (undefined, null, '')
 * so React can omit the href attribute entirely.
 *
 * Decodes URL encoding once to prevent bypass attacks where dangerous protocols are
 * encoded (e.g., %6A%61%76%61%73%63%72%69%70%74: → javascript:).
 *
 * Takes a URL to sanitize (from config, API, or user input) and an optional list of
 * allowed schemes (defaults to ['http:', 'https:', 'mailto:', 'tel:', 'sms:']). Returns the
 * sanitized URL, '#' for blocked schemes, or the original falsy value.
 *
 * @example
 * sanitizeHref('https://example.com') // → 'https://example.com'
 * sanitizeHref('javascript:alert(1)') // → '#'
 * sanitizeHref('%6A%61%76%61%73%63%72%69%70%74:alert(1)') // → '#' (decoded and blocked)
 * sanitizeHref('tel:5555555555') // → 'tel:5555555555'
 * sanitizeHref(undefined) // → undefined
 * sanitizeHref('') // → ''
 */
export function sanitizeHref(
  url: string | null | undefined,
  allowedSchemes: readonly string[] = DEFAULT_ALLOWED_URL_SCHEMES
): string | undefined {
  if (!url) {
    return undefined;
  }

  const trimmed = url.trim();

  let decoded = trimmed;
  try {
    // attempt to decode the URL in case the URL contains encoded javascript scheme
    decoded = decodeURIComponent(trimmed);
  } catch {
    decoded = trimmed;
  }

  try {
    const parsed = new URL(decoded);

    if (allowedSchemes.includes(parsed.protocol.toLowerCase())) {
      // SAFE, return the original URL to preserve encoding
      return trimmed;
    }

    // block potentially unsafe URLs by returning # instead of whatever was passed in
    return '#';
  } catch {
    // URL constructor failed, so url is a relative URL or falsy value (both safe)
    return trimmed;
  }
}
